/**
 * AddContactHandleButton
 *
 * Minimalist button to add a new contact handle to the current carrier.
 * Passes divisionId directly to addHandle to avoid "no default division" error.
 */

import { Loader2, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAddHandleMutation,
  useListDivisionsQuery,
} from '@/services/api/carriersApiService';

import {
  ContactHandleForm,
  ContactHandleFormState,
  INITIAL_FORM_STATE,
} from './ContactHandleForm';

interface AddContactHandleButtonProps {
  carrierId: string;
  carrierName: string;
}

export const AddContactHandleButton: React.FC<AddContactHandleButtonProps> = ({
  carrierId,
  carrierName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] =
    useState<ContactHandleFormState>(INITIAL_FORM_STATE);

  const [addHandle, { isLoading: isAdding }] = useAddHandleMutation();
  const { data: divisions } = useListDivisionsQuery(
    { carrierId },
    { skip: !isOpen },
  );

  const handleClose = () => {
    setFormState(INITIAL_FORM_STATE);
    setIsOpen(false);
  };

  const handleSave = async () => {
    const isAddress = formState.handleType === 'MAILING_ADDRESS';
    const defaultDivision =
      divisions?.find((d) => d.isDefault) ?? divisions?.[0];

    if (!defaultDivision) {
      toast.error('Carrier has no division. Please add one first.');
      return;
    }

    try {
      // Pass divisionId directly - backend will auto-link the handle
      await addHandle({
        carrierId,
        divisionId: defaultDivision.id,
        handleType: formState.handleType,
        value: isAddress ? undefined : formState.value.trim(),
        name: formState.label.trim() || undefined,
        addressLine1: isAddress ? formState.addressLine1.trim() : undefined,
        addressLine2: isAddress
          ? formState.addressLine2.trim() || undefined
          : undefined,
        city: isAddress ? formState.city.trim() : undefined,
        state: isAddress ? formState.state : undefined,
        zip: isAddress ? formState.zip.trim() : undefined,
      }).unwrap();

      toast.success('Contact added');
      handleClose();
    } catch (error) {
      console.error('Failed to add contact:', error);
      toast.error('Failed to add contact');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-indigo-600 transition-colors"
      >
        <Plus className="h-3 w-3" />
        Add
      </button>

      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Plus className="w-4 h-4 text-zinc-500" />
              Add Contact
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Directory update notice */}
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
              <span className="text-[11px] text-amber-700">
                This will add a new contact to{' '}
                <span className="font-medium">{carrierName}</span> in the
                carrier directory.
              </span>
            </div>

            <ContactHandleForm state={formState} onChange={setFormState} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isAdding}>
              {isAdding && (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              )}
              Add Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
